import { Car, Expense, LogEntry, supabase } from './supabase';

type DataCache = {
    cars: Car[] | null;
    favoriteCars: Car[] | null;
    logEntries: Record<string, LogEntry[]>;
    gallery: Record<string, any[]>;
    lastFetched: Record<string, number>;
};

const CACHE_TTL = 1000 * 60 * 5; // 5 minutes

class DataManager {
    private static instance: DataManager;
    private cache: DataCache & { userPreferences: Record<string, any> } = {
        cars: null,
        favoriteCars: null,
        logEntries: {},
        gallery: {},
        lastFetched: {},
        userPreferences: {},
    };

    private constructor() {
        this.loadFromLocalStorage();
    }

    public static getInstance(): DataManager {
        if (!DataManager.instance) {
            DataManager.instance = new DataManager();
        }
        return DataManager.instance;
    }

    private loadFromLocalStorage() {
        try {
            const saved = localStorage.getItem('car_data_cache');
            if (saved) {
                const parsed = JSON.parse(saved);
                this.cache = {
                    cars: parsed.cars || null,
                    favoriteCars: parsed.favoriteCars || null,
                    logEntries: parsed.logEntries || {},
                    gallery: parsed.gallery || {},
                    lastFetched: parsed.lastFetched || {},
                    userPreferences: parsed.userPreferences || {}
                };
            }
        } catch (e) {
            console.error('Failed to load cache from localStorage', e);
        }
    }

    private saveToLocalStorage() {
        try {
            localStorage.setItem('car_data_cache', JSON.stringify(this.cache));
        } catch (e) {
            console.error('Failed to save cache to localStorage', e);
        }
    }


    public getCachedCars(userId: string): Car[] | null {
        const cacheKey = `cars_${userId}`;
        const now = Date.now();
        if (this.cache.cars && (now - (this.cache.lastFetched[cacheKey] || 0) < CACHE_TTL)) {
            return this.cache.cars;
        }
        return null;
    }

    public getCachedLogEntries(carId: string): LogEntry[] | null {
        return this.cache.logEntries[carId] || null;
    }

    public async fetchCars(userId: string, force = false): Promise<Car[]> {
        const now = Date.now();
        const cacheKey = `cars_${userId}`;
        if (!force && this.cache.cars && (now - (this.cache.lastFetched[cacheKey] || 0) < CACHE_TTL)) {
            return this.cache.cars;
        }

        const { data, error } = await supabase
            .from('cars')
            .select('*')
            .eq('user_id', userId)
            .order('created_at', { ascending: false });

        if (error) throw error;

        this.cache.cars = data || [];
        this.cache.lastFetched[cacheKey] = now;
        this.saveToLocalStorage();
        return this.cache.cars;
    }

    public async fetchAllCars(): Promise<Car[]> {
        // Try to fetch with owner emails using RPC
        try {
            const { data, error } = await supabase.rpc('get_admin_cars');
            if (!error && data) {
                return data as Car[];
            }
        } catch (e) {
            console.warn('Failed to fetch admin cars via RPC', e);
        }

        // Fallback to regular fetch
        const { data, error } = await supabase
            .from('cars')
            .select('*')
            .order('created_at', { ascending: false });

        if (error) throw error;
        return data || [];
    }

    public async fetchLogEntries(carId: string, force = false): Promise<LogEntry[]> {
        const now = Date.now();
        const cacheKey = `log_entries_${carId}`;
        if (!force && this.cache.logEntries[carId] && (now - (this.cache.lastFetched[cacheKey] || 0) < CACHE_TTL)) {
            return this.cache.logEntries[carId];
        }

        const { data, error } = await supabase
            .from('log_entries')
            .select(`
                *,
                expenses (*),
                log_entry_images(id),
                log_entry_parts(spare_part_id)
            `)
            .eq('car_id', carId)
            .order('date', { ascending: false });

        if (error) throw error;

        const entriesWithCounts = (data || []).map((le: any) => ({
            ...le,
            imageCount: le.log_entry_images?.length || 0,
            partCount: le.log_entry_parts?.length || 0
        }));

        this.cache.logEntries[carId] = entriesWithCounts;
        this.cache.lastFetched[cacheKey] = now;
        this.saveToLocalStorage();
        return this.cache.logEntries[carId];
    }

    public getCachedGallery(carId: string): any[] | null {
        const cached = this.cache.gallery[carId];
        if (cached && cached.length > 0 && !cached[0].type) return null;
        return cached || null;
    }

    public async fetchGallery(carId: string, force = false): Promise<any[]> {
        const now = Date.now();
        const cacheKey = `gallery_${carId}`;
        const cached = this.cache.gallery[carId];
        const isLegacyCache = cached && cached.length > 0 && !cached[0].type;

        if (!force && !isLegacyCache && cached && (now - (this.cache.lastFetched[cacheKey] || 0) < CACHE_TTL)) {
            return cached;
        }

        const [expenseImagesResult, sparePartImagesResult] = await Promise.all([
            supabase
                .from('log_entry_images')
                .select('*, log_entries!inner(car_id)')
                .eq('log_entries.car_id', carId)
                .order('created_at', { ascending: false }),
            supabase
                .from('spare_part_images')
                .select('*, spare_parts!inner(car_id)')
                .eq('spare_parts.car_id', carId)
                .order('created_at', { ascending: false })
        ]);

        if (expenseImagesResult.error) throw expenseImagesResult.error;
        if (sparePartImagesResult.error) throw sparePartImagesResult.error;

        const expenseImages = (expenseImagesResult.data || []).map(img => ({
            id: img.id,
            image_url: img.image_url,
            image_url_thumb: img.image_url_thumb,
            comment: img.comment,
            created_at: img.created_at,
            log_entry_id: img.log_entry_id,
            type: 'expense' as const
        }));

        const sparePartImages = (sparePartImagesResult.data || []).map(img => ({
            id: img.id,
            image_url: img.image_url,
            image_url_thumb: img.image_url_thumb,
            comment: img.comment,
            created_at: img.created_at,
            spare_part_id: img.spare_part_id,
            type: 'part' as const
        }));

        const allImages = [...expenseImages, ...sparePartImages].sort(
            (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );

        this.cache.gallery[carId] = allImages;
        this.cache.lastFetched[cacheKey] = now;
        this.saveToLocalStorage();
        return allImages;
    }

    public invalidateGallery(carId: string) {
        delete this.cache.gallery[carId];
        delete this.cache.lastFetched[`gallery_${carId}`];
        this.saveToLocalStorage();
    }

    public invalidateCars(userId?: string) {
        this.cache.cars = null;
        if (userId) {
            delete this.cache.lastFetched[`cars_${userId}`];
        }
        this.saveToLocalStorage();
    }

    public invalidateFavorites(userId: string) {
        this.cache.favoriteCars = null;
        delete this.cache.lastFetched[`favorites_${userId}`];
        this.saveToLocalStorage();
    }

    public updateCarCache(car: Car) {
        if (!this.cache.cars) this.cache.cars = [];
        const index = this.cache.cars.findIndex(c => c.id === car.id);
        if (index >= 0) {
            this.cache.cars[index] = car;
        } else {
            this.cache.cars.unshift(car);
        }
        this.saveToLocalStorage();
    }

    public updateLogEntryCache(carId: string, logEntry: LogEntry) {
        if (!this.cache.logEntries[carId]) return;
        const index = this.cache.logEntries[carId].findIndex(le => le.id === logEntry.id);
        if (index >= 0) {
            this.cache.logEntries[carId][index] = logEntry;
        } else {
            this.cache.logEntries[carId].unshift(logEntry);
        }
        this.saveToLocalStorage();
    }

    public updateLogEntriesCache(carId: string, logEntries: LogEntry[]) {
        this.cache.logEntries[carId] = logEntries;
        this.cache.lastFetched[`log_entries_${carId}`] = Date.now();
        this.saveToLocalStorage();
    }

    public invalidateLogEntries(carId: string) {
        delete this.cache.logEntries[carId];
        delete this.cache.lastFetched[`log_entries_${carId}`];
        this.saveToLocalStorage();
    }

    public clearCache() {
        this.cache = {
            cars: null,
            favoriteCars: null,
            logEntries: {},
            gallery: {},
            lastFetched: {},
            userPreferences: {},
        };
        localStorage.removeItem('car_data_cache');
    }

    public async fetchFavorites(userId: string, force = false): Promise<Car[]> {
        const now = Date.now();
        const cacheKey = `favorites_${userId}`;

        if (!force && this.cache.favoriteCars && (now - (this.cache.lastFetched[cacheKey] || 0) < CACHE_TTL)) {
            return this.cache.favoriteCars;
        }

        const { data, error } = await supabase
            .from('favorites')
            .select('cars(*)')
            .eq('user_id', userId)
            .order('created_at', { ascending: false });

        if (error) throw error;

        // Extract cars from the join result
        // Supabase returns an array of objects like { cars: { ...carData } }
        // We need to cast and map it.
        const favoriteCars = (data || [])
            .map((item: any) => item.cars)
            .filter((car: any) => car !== null) // Filter out nulls if any join failed (shouldn't happen with inner join logic match)
            .map((car: any) => ({
                ...car,
                is_favorite: true
            }));

        this.cache.favoriteCars = favoriteCars;
        this.cache.lastFetched[cacheKey] = now;
        this.saveToLocalStorage();
        return favoriteCars;
    }

    public async addFavorite(userId: string, carId: string): Promise<void> {
        const { error } = await supabase
            .from('favorites')
            .insert({ user_id: userId, car_id: carId });

        if (error) throw error;

        // Invalidate favorites cache
        delete this.cache.lastFetched[`favorites_${userId}`];
        // Could realistically refetch or manually update array, but refetch is safer for consistency
    }

    public async reorderCars(userId: string, orderedIds: string[]): Promise<void> {
        // Optimistic update
        if (this.cache.cars) {
            const cars = this.cache.cars;
            orderedIds.forEach((id, index) => {
                const car = cars.find(c => c.id === id);
                if (car) car.sort_order = index;
            });
            // No need to sort array itself if we rely on sort_order property later, 
            // but keeping it consistent is good.
            this.cache.cars.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
            this.saveToLocalStorage();
        }

        // DB Update
        const updates = orderedIds.map((id, index) =>
            supabase.from('cars').update({ sort_order: index }).eq('id', id)
        );

        await Promise.all(updates);

        // Invalidate fetching to be safe? 
        delete this.cache.lastFetched[userId];
    }

    public async removeFavorite(userId: string, carId: string): Promise<void> {
        const { error } = await supabase
            .from('favorites')
            .delete()
            .match({ user_id: userId, car_id: carId });

        if (error) throw error;

        // Keep cache consistent locally
        if (this.cache.favoriteCars) {
            this.cache.favoriteCars = this.cache.favoriteCars.filter(c => c.id !== carId);
            this.saveToLocalStorage();
        }
    }

    public async fetchUserPreferences(userId: string): Promise<any> {
        if (this.cache.userPreferences[userId]) {
            return this.cache.userPreferences[userId];
        }

        const { data, error } = await supabase
            .from('user_preferences')
            .select('*')
            .eq('user_id', userId)
            .maybeSingle();

        if (error) throw error;

        if (data) {
            this.cache.userPreferences[userId] = data;
            this.saveToLocalStorage();
        }
        return data;
    }

    public updateUserPreferencesCache(userId: string, prefs: any) {
        this.cache.userPreferences[userId] = prefs;
        this.saveToLocalStorage();
    }
}

export const dataManager = DataManager.getInstance();
